import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiService } from '../../shared/services/api.service';
import { take } from 'rxjs';

export interface VoiceFallbackDialogData {
  customerId: string;
  planNumber?: string;
  // The connectionId of the transcript row this dialog was opened from.
  // Assumed to be the same identifier the fallback endpoints call
  // voiceSessionId (one Nova Sonic voice session = one WebSocket
  // connection). Sent as a request param, and also used to filter the
  // dialog down to just this one session client-side.
  voiceSessionId?: string;
}

interface FallbackTextMessage {
  role: string;
  message: string;
  timestamp: string;
}

interface FallbackTrace {
  customerId: string;
  planNumber?: string;
  voiceSessionId: string;
  voiceSessionStart: string;
  voiceSessionEnd: string;
  voiceSessionDuration: number;
  voiceSessionKeepalives: number;
  voiceMessageCount: number;
  fallbackOccurred: boolean;
  fallbackReason?: string;
  // Confirmed via real API response: the trace endpoint now reports how it
  // matched a fallback directly, so there's no need to cross-reference
  // trace-journey-with-fallback-details separately anymore.
  matchedBy?: 'voiceSessionId' | 'timeWindow';
  textSessionId?: string;
  textSessionStart?: string;
  timeSinceFallbackMs?: number;
  textMessageCount?: number;
  textMessages?: FallbackTextMessage[];
  // Derived client-side from matchedBy, for the Match Type column.
  idConfirmed?: boolean;
  // Derived client-side: does this row match the transcript the dialog was
  // opened from (VoiceFallbackDialogData.voiceSessionId)?
  isCurrentSession?: boolean;
}

interface FallbackTraceMetadata {
  totalVoiceSessions?: number;
  totalFallbacks?: number;
  fallbackRate?: string;
  fallbackWindowMs?: number;
}

interface FallbackTraceResponseBody {
  data?: FallbackTrace[];
  metadata?: FallbackTraceMetadata;
  // Older assumed shape, kept for tolerance in case an environment still
  // returns it.
  traces?: FallbackTrace[];
  totalVoiceSessions?: number;
  totalFallbacks?: number;
  fallbackRate?: string;
}

interface FallbackTraceResponse {
  statusCode: number;
  body: FallbackTraceResponseBody;
}

const FALLBACK_WINDOW_OPTIONS = [
  { value: 300000, label: '5 minutes' },
  { value: 900000, label: '15 minutes' },
  { value: 1800000, label: '30 minutes' },
  { value: 3600000, label: '1 hour' },
];

@Component({
  selector: 'app-voice-fallback-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
  ],
  templateUrl: './voice-fallback-dialog.component.html',
  styleUrls: ['./voice-fallback-dialog.component.scss'],
})
export class VoiceFallbackDialogComponent {
  private apiService = inject(ApiService);
  dialogRef = inject(MatDialogRef<VoiceFallbackDialogComponent>);
  data = inject<VoiceFallbackDialogData>(MAT_DIALOG_DATA);

  isLoading = signal(false);
  hasSearchedOnce = signal(false);
  errorMessage = signal<string | null>(null);

  traces = signal<FallbackTrace[]>([]);
  totalVoiceSessions = signal(0);
  totalFallbacks = signal(0);
  fallbackRate = signal('0%');
  currentSessionFound = signal(false);
  expandedSessionId = signal<string | null>(null);

  fallbackWindowMs = 1800000;
  fallbackWindowOptions = FALLBACK_WINDOW_OPTIONS;

  displayedColumns = [
    'voiceSessionId',
    'voiceStart',
    'duration',
    'keepalives',
    'voiceMessages',
    'fallback',
    'matchType',
    'timeToFallback',
    'textMessages',
  ];

  constructor() {
    this.fetchTraces();
  }

  fetchTraces() {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    const payload: Record<string, string | number> = {
      customerId: this.data.customerId,
      fallbackWindowMs: this.fallbackWindowMs,
    };

    if (this.data.planNumber) {
      payload['planNumber'] = this.data.planNumber;
    }

    if (this.data.voiceSessionId) {
      payload['voiceSessionId'] = this.data.voiceSessionId;
    }

    this.apiService
      .post<
        FallbackTraceResponse | FallbackTraceResponseBody,
        Record<string, string | number>
      >('admin/trace-voice-to-text-fallback', payload)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const body: FallbackTraceResponseBody =
            'body' in response && response.body
              ? response.body
              : (response as FallbackTraceResponseBody);

          const rawTraces = body?.data || body?.traces || [];
          const metadata = body?.metadata;

          const currentSessionId = this.data.voiceSessionId;
          const allTraces: FallbackTrace[] = rawTraces.map((trace) => ({
            ...trace,
            idConfirmed: trace.matchedBy === 'voiceSessionId',
            isCurrentSession: Boolean(
              currentSessionId && trace.voiceSessionId === currentSessionId
            ),
          }));

          const matched = currentSessionId
            ? allTraces.filter((t) => t.isCurrentSession)
            : [];

          this.currentSessionFound.set(matched.length > 0);
          this.traces.set(matched.length > 0 ? matched : allTraces);

          this.totalVoiceSessions.set(
            metadata?.totalVoiceSessions ?? body?.totalVoiceSessions ?? 0
          );
          this.totalFallbacks.set(
            metadata?.totalFallbacks ?? body?.totalFallbacks ?? 0
          );
          this.fallbackRate.set(
            metadata?.fallbackRate ?? body?.fallbackRate ?? '0%'
          );
          this.expandedSessionId.set(null);
          this.hasSearchedOnce.set(true);
          this.isLoading.set(false);
        },
        error: (err: HttpErrorResponse) => {
          console.error('Error tracing voice-to-text fallbacks:', err);
          const message = err.error?.message || err.message || 'Unknown error';
          this.isLoading.set(false);
          this.errorMessage.set(
            `Failed to trace fallback sessions. ${message}`
          );
        },
      });
  }

  toggleExpanded(trace: FallbackTrace) {
    this.expandedSessionId.set(
      this.expandedSessionId() === trace.voiceSessionId
        ? null
        : trace.voiceSessionId
    );
  }

  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateString;
    }
  }

  formatDuration(milliseconds: number): string {
    if (!milliseconds) return '0s';

    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  }
}
